/**
 * URL-state helpers. The URL is the share format: every recommendation can
 * be hydrated from the URL alone, so sharing a link recreates the exact view
 * the sharer was looking at.
 *
 * Today: /?city=Uppsala&when=now
 * Trip:  /trip?trip=Uppsala:2026-05-26,Amsterdam:2026-05-28&home=2026-05-29
 */

const WHEN_FORECAST_HOURS = {
  now: 0,
  '3h': 3,
  '6h': 6,
  '8h': 8,
  '12h': 12,
  yesterday: -24
};

const FORECAST_HOURS_TO_WHEN = Object.fromEntries(
  Object.entries(WHEN_FORECAST_HOURS).map(([k, v]) => [v, k])
);

/** Read ?city + ?when. Returns null when not present. */
export const readTodayFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const city = params.get('city');
  const whenParam = params.get('when');
  if (!city) return null;
  return {
    city,
    forecastHours: whenParam != null && whenParam in WHEN_FORECAST_HOURS
      ? WHEN_FORECAST_HOURS[whenParam]
      : 0
  };
};

/** Push ?city / ?when into the URL without a navigation. */
export const writeTodayToUrl = ({ city, forecastHours }) => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams();
  if (city) params.set('city', city);
  const whenKey = FORECAST_HOURS_TO_WHEN[forecastHours];
  if (whenKey && whenKey !== 'now') params.set('when', whenKey);
  const query = params.toString();
  const next = `${window.location.pathname}${query ? '?' + query : ''}`;
  window.history.replaceState(null, '', next);
};

/** Build a canonical share URL for a Today recommendation. Returns the
 *  /share landing URL so social crawlers see a proper preview; humans
 *  hitting the link get redirected to the SPA via meta refresh. */
export const buildTodayShareUrl = ({ city, forecastHours, origin = 'https://layers.today' }) => {
  const params = new URLSearchParams();
  if (city) params.set('city', city);
  const whenKey = FORECAST_HOURS_TO_WHEN[forecastHours];
  // Include `when` even for "now" so the share landing has unambiguous params.
  if (whenKey) params.set('when', whenKey);
  return `${origin}/share?${params.toString()}`;
};

// Stop separator: `|` (pipe). Was `,` originally but city names from
// Open-Meteo include their country/admin suffix ("Stockholm, Sweden"), so
// a comma-separator collided with the comma already inside the name and
// broke shared trip links — the recipient got a different trip from the
// sender. Pipe is unambiguous and URL-safe (URLSearchParams encodes it
// to %7C on write and decodes on read).
const STOP_SEP = '|';

/** Read ?trip=City:YYYY-MM-DD|...&home=YYYY-MM-DD&home_city=Name. Returns null when no trip. */
export const readTripFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const trip = params.get('trip');
  const home = params.get('home');
  const homeCity = params.get('home_city');
  if (!trip) return null;
  // Support legacy ',' separator for any link shared before the switch:
  // if there's no '|' but at least one ':', try splitting on ','. (Will
  // misparse some legacy "City, Suffix:Date" links; that's an existing
  // bug, not a new one.)
  const separator = trip.includes(STOP_SEP) ? STOP_SEP : ',';
  const stops = trip.split(separator).map((part) => {
    const idx = part.lastIndexOf(':');
    if (idx < 0) return { name: part, arrivalDate: null };
    const name = part.slice(0, idx);
    const dateStr = part.slice(idx + 1);
    const arrivalDate = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? new Date(`${dateStr}T00:00:00`) : null;
    return { name, arrivalDate };
  }).filter((s) => s.name && s.arrivalDate);
  const returnDate = home && /^\d{4}-\d{2}-\d{2}$/.test(home) ? new Date(`${home}T00:00:00`) : null;
  if (!stops.length) return null;
  return { stops, returnDate, homeName: homeCity || null };
};

/** Push ?trip / ?home / ?home_city into the URL. */
export const writeTripToUrl = ({ stops, returnDate, homeName, formatDate }) => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams();
  const tripParts = stops
    .filter((s) => s.name && s.arrivalDate)
    .map((s) => `${s.name}:${formatDate(s.arrivalDate)}`);
  if (tripParts.length) params.set('trip', tripParts.join(STOP_SEP));
  if (returnDate) params.set('home', formatDate(returnDate));
  if (homeName) params.set('home_city', homeName);
  const query = params.toString();
  const next = `${window.location.pathname}${query ? '?' + query : ''}`;
  window.history.replaceState(null, '', next);
};

/** Build a canonical share URL for a Trip plan. Returns the /share/trip
 *  landing URL so social crawlers see a proper preview; humans hitting
 *  the link get redirected to the SPA via meta refresh. */
export const buildTripShareUrl = ({ stops, returnDate, homeName, formatDate, origin = 'https://layers.today' }) => {
  const params = new URLSearchParams();
  const tripParts = stops
    .filter((s) => s.name && s.arrivalDate)
    .map((s) => `${s.name}:${formatDate(s.arrivalDate)}`);
  if (tripParts.length) params.set('trip', tripParts.join(STOP_SEP));
  if (returnDate) params.set('home', formatDate(returnDate));
  if (homeName) params.set('home_city', homeName);
  const query = params.toString();
  return `${origin}/share/trip${query ? '?' + query : ''}`;
};
